import * as React from "react";
import { cn } from "@/lib/utils";

export interface ProfileSkeletonProps
  extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * Loading skeleton for ProfilePage component
 *
 * Matches ProfilePage layout with animated pulse effect.
 * Used to show loading state while fetching user profile data.
 *
 * Shows:
 * - Avatar section (center-aligned)
 * - Username field
 * - 3 social link skeleton fields (ANYW3AR standard)
 * - 3 wallet group skeletons (ETH, SOL, BTC)
 *
 * @example
 * ```tsx
 * <ProfileSkeleton />
 * ```
 */
export const ProfileSkeleton = React.forwardRef<
  HTMLDivElement,
  ProfileSkeletonProps
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      aria-label="Loading profile"
      className={cn("animate-pulse", className)}
      {...props}
    >
      {/* Avatar Section */}
      <div className="mb-8 mr-9 flex flex-col items-center gap-4">
        {/* Avatar placeholder */}
        <div className="size-32 bg-gray-100 rounded-full" />

        {/* Upload button placeholder */}
        <div className="h-10 bg-gray-100 rounded w-32" />
      </div>

      {/* Username Field */}
      <div className="mb-4 flex items-center gap-2">
        {/* Username text placeholder */}
        <div className="flex-1 h-8 bg-gray-100 rounded w-48 mx-auto" />

        {/* Edit button placeholder */}
        <div className="size-8 bg-gray-100 rounded" />
      </div>

      {/* Social Links Section */}
      <div className="mb-8">
        {/* Section title */}
        <div className="h-6 bg-gray-100 rounded w-32 mb-4" />

        {/* 3 social link field skeletons */}
        <div className="flex flex-col gap-4">
          {[...Array(3)].map((_, index) => (
            <div key={`social-${index}`} className="flex items-center gap-3">
              {/* Icon placeholder */}
              <div className="size-6 bg-gray-100 rounded" />

              {/* Input field placeholder */}
              <div className="flex-1 h-12 bg-gray-100 rounded" />

              {/* Action buttons placeholder */}
              <div className="flex gap-2">
                <div className="size-8 bg-gray-100 rounded" />
                <div className="size-8 bg-gray-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Wallet Management Section */}
      <div className="mb-8">
        {/* Section title */}
        <div className="h-6 bg-gray-100 rounded w-32 mb-4" />

        {/* 3 wallet group skeletons (ETH, SOL, BTC) */}
        <div className="flex flex-col gap-4">
          {[...Array(3)].map((_, index) => (
            <div key={`wallet-${index}`} className="flex flex-col gap-2">
              {/* Wallet group header (expandable) */}
              <div className="flex items-center justify-between p-4 bg-gray-100 rounded">
                {/* Wallet icon + name placeholder */}
                <div className="flex items-center gap-3">
                  <div className="size-6 bg-gray-300 rounded" />
                  <div className="h-6 bg-gray-300 rounded w-24" />
                </div>

                {/* Collapse indicator placeholder */}
                <div className="size-6 bg-gray-300 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delete Account Button Placeholder */}
      <div className="mt-12">
        <div className="h-12 bg-gray-100 rounded w-full" />
      </div>
    </div>
  );
});

ProfileSkeleton.displayName = "ProfileSkeleton";
